// Workforce › Digital employees (`/c/{slug}/agents`; M3-08, Agents UX pass 2026-10-21;
// WORKFORCE 2026-10-26: titled "Digital employees", each row badged DE): the roster, a dense
// list of the company's digital employees. One 36 px row per DE: avatar, name, seat, readiness (DE-WIZARD: Draft
// / Awaiting approval / Deploying / Live / Not live, the reason in the meta line until it is
// live), what it is doing now, today's runs and cost, and the hover actions (Message, Pause /
// Resume, Runs, Keys, Cost). The header carries the count, the approvals waiting for the caller
// as a pill, and Add (the one "Add digital employee" flow). No hero, no stat tiles, no subtitle. Retired DEs are hidden by
// default; the toolbar's "Show retired (n)" toggle (only when there are some) lists them.
// Calls on load: the DE list; with `agents`, the latest runs and the caller's approvals (shared
// with the sidebar's count) and the cached company detail (currency). Without the add-on the
// real DEs list under the add-on banner, and nothing else (no sample rows).
import {
  AvatarTile,
  Badge,
  Button,
  buttonVariants,
  CountPill,
  cn,
  EmptyState,
  ListRow,
  LocationHeader,
  SearchField,
  Toolbar,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ActivityIcon,
  BotIcon,
  EraserIcon,
  KeyRoundIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
  WalletIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { DeSummary, RunRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { useWalletOn } from "../billing/use-wallet-on";
import { type CompanyAccess, isManager } from "../company";
import { MOVES, useLifecycle } from "../de/de-lifecycle";
import { ReadinessBadge, readinessReason } from "../de/readiness";
import { deployedUnverified } from "../de/unverified-badge";
import { digitalEmployeesQuery, keys } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { useFeature } from "../use-feature";
import { formatElapsed, isLive, RUN_STATE_LABELS, UsageCost, useCostCurrency } from "./agent-parts";
import { myApprovalsQuery, ROSTER_RUNS, runsQuery } from "./agent-queries";
import {
  AgentModeSwitch,
  AgentRequestsSection,
  RequestAgentButton,
  useAgentMode,
} from "./agent-requests";
import {
  AGENT_STATUS_LABELS,
  AgentsOffBanner,
  agentStatus,
  LIST_BODY,
  MessageAction,
  RowAction,
  RowLinkAction,
} from "./agents-ux";
import { usePurge } from "./purge-dialog";

/** Each DE's latest run (the runs come newest first). */
export function latestRuns(runs: readonly RunRow[]): Map<string, RunRow> {
  const out = new Map<string, RunRow>();
  for (const r of runs) {
    const seen = out.get(r.membership_id);
    // A live run outranks a newer ended one: it is what the DE is doing now.
    if (!seen || (!isLive(seen.state) && isLive(r.state))) out.set(r.membership_id, r);
  }
  return out;
}

/** Today's runs and metered cost per DE, from the latest runs (local midnight on). */
export function todayByDe(runs: readonly RunRow[], now = new Date()): Map<string, DayUsage> {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const out = new Map<string, DayUsage>();
  for (const r of runs) {
    if (Date.parse(r.created_at) < start) continue;
    const t = out.get(r.membership_id) ?? emptyUsage();
    addUsage(t, r);
    out.set(r.membership_id, t);
  }
  return out;
}

type DayUsage = { runs: number; cost_micros: number; tokens_in: number; tokens_out: number };

function emptyUsage(): DayUsage {
  return { runs: 0, cost_micros: 0, tokens_in: 0, tokens_out: 0 };
}

function addUsage(t: DayUsage, u: Omit<DayUsage, "runs"> & { runs?: number }) {
  t.runs += u.runs ?? 1;
  t.cost_micros += u.cost_micros;
  t.tokens_in += u.tokens_in;
  t.tokens_out += u.tokens_out;
}

type Live = {
  latest: Map<string, RunRow>;
  today: Map<string, DayUsage>;
  currency: string | undefined;
  walletOn: boolean;
  loading: boolean;
};

export function RosterScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const agents = useFeature("agents");
  // Retired DEs are hidden unless "Show retired" is on (their profiles stay reachable by link);
  // then removed ones are listed too, so they can be removed permanently.
  const [showRetired, setShowRetired] = useState(false);
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id, showRetired));
  const runs = useInfiniteQuery({
    ...runsQuery(api, company.id, {}, ROSTER_RUNS),
    enabled: agents.enabled,
  });
  const mine = useQuery({ ...myApprovalsQuery(api, company.id), enabled: agents.enabled });
  const currency = useCostCurrency(api, company.id, agents.enabled);
  const walletOn = useWalletOn(api, company.id, agents.enabled);
  const [search, setSearch] = useState("");
  const all = des.data?.pages.flatMap((p) => p.data) ?? [];
  const retiredCount = all.filter((d) => d.status === "retired").length;
  const rows = showRetired ? all : all.filter((d) => d.status !== "retired");
  const manager = isManager(company);
  const mode = useAgentMode(api, company);
  const recent = runs.data?.pages[0]?.data ?? [];
  const latest = useMemo(() => latestRuns(recent), [recent]);
  const today = useMemo(() => todayByDe(recent), [recent]);
  const live: Live | null = agents.enabled
    ? { latest, today, currency, walletOn, loading: runs.isPending }
    : null;
  const q = search.trim().toLowerCase();
  const shown = q
    ? rows.filter(
        (d) =>
          d.display_name.toLowerCase().includes(q) ||
          (d.seat?.title ?? "").toLowerCase().includes(q),
      )
    : rows;
  const counts = { live: 0, idle: 0, paused: 0, setup: 0, retired: 0, not_deployed: 0 };
  for (const d of rows) counts[agentStatus(d.status, latest.get(d.membership_id))] += 1;
  const todayTotal = emptyUsage();
  for (const t of today.values()) addUsage(todayTotal, t);
  const todayRuns = todayTotal.runs;
  const waiting = mine.data?.data.length ?? 0;
  const params = { slug: company.slug };
  return (
    <div className={LIST_BODY} data-slot="roster">
      <LocationHeader
        icon={BotIcon}
        title="Digital employees"
        {...(des.isSuccess ? { count: rows.length } : {})}
        badge={
          waiting ? (
            <Link
              to="/c/$slug/agents/approvals"
              params={params}
              search={keepFocus}
              className="inline-flex items-center gap-1 rounded-md text-xs text-warning-text hover:underline"
              aria-label={`${waiting} approval${waiting === 1 ? "" : "s"} waiting for you`}
              data-slot="waiting-pill"
            >
              <CountPill count={waiting} tone="warning" aria-hidden="true" />
              waiting
            </Link>
          ) : null
        }
        actions={
          <span className="flex items-center gap-3">
            {/* MANAGED-POLICY: an agency admin switches who adds agents here. */}
            <AgentModeSwitch api={api} company={company} />
            {manager && mode.ready && !mode.canAdd ? (
              <RequestAgentButton api={api} company={company} kind="de" variant="labelled" />
            ) : manager && mode.ready ? (
              <Link
                to="/c/$slug/setup/des/new"
                params={params}
                search={keepFocus}
                className={cn(buttonVariants({ size: "sm" }))}
              >
                <PlusIcon aria-hidden="true" />
                Add
              </Link>
            ) : null}
          </span>
        }
      />
      {agents.enabled ? null : <AgentsOffBanner company={company} />}
      {/* MANAGED-POLICY: the requests sent to the agency; kept after a switch to self-serve
          (the section hides itself while there are none). */}
      {mode.ready ? (
        <AgentRequestsSection api={api} company={company} className="px-6 pt-3 max-md:px-4" />
      ) : null}
      <Toolbar bleed aria-label="Agents tools">
        <SearchField
          value={search}
          placeholder="Search agents…"
          aria-label="Search agents"
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums" aria-live="polite">
          {(["live", "idle", "paused", "setup", "not_deployed"] as const)
            .filter((k) => counts[k])
            .map((k) => `${counts[k]} ${AGENT_STATUS_LABELS[k].toLowerCase()}`)
            .join(" · ") || (des.isSuccess ? "No agents" : "…")}
        </span>
        {/* Managers always: a removed DE is listed only once it is on (to remove it for good). */}
        {retiredCount || showRetired || manager ? (
          <Button
            variant="ghost"
            size="xs"
            aria-pressed={showRetired}
            className="shrink-0 aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
            onClick={() => setShowRetired((v) => !v)}
            data-slot="show-retired"
          >
            Show retired{retiredCount ? ` (${retiredCount})` : ""}
          </Button>
        ) : null}
        {live ? (
          <span
            className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums"
            data-slot="today-total"
          >
            {live.loading ? (
              "Today: …"
            ) : todayRuns ? (
              <>
                {`Today: ${todayRuns} run${todayRuns === 1 ? "" : "s"} · `}
                <UsageCost usage={todayTotal} currency={currency} hideTokens={walletOn} />
              </>
            ) : (
              "Today: 0 runs"
            )}
          </span>
        ) : null}
      </Toolbar>
      {des.isPending ? (
        <div className="px-6 py-4 max-md:px-4">
          <Loading label="Loading digital employees" rows={3} />
        </div>
      ) : des.isError ? (
        <div className="px-6 py-4 max-md:px-4">
          <LoadError what="Digital employees" error={des.error} />
        </div>
      ) : !rows.length ? (
        <EmptyState
          variant="compact"
          icon={BotIcon}
          title={retiredCount ? "No active agents" : "No agents yet"}
          description={
            retiredCount
              ? "Retired agents are hidden; Show retired lists them."
              : manager
                ? "Add sets one up on a seat."
                : "An agent appears here once a manager sets it up."
          }
          actions={
            !retiredCount && manager && mode.ready && !mode.canAdd ? (
              // MANAGED-POLICY: a managed company asks its agency for a DE.
              <RequestAgentButton api={api} company={company} kind="de" variant="labelled" />
            ) : !retiredCount && manager && mode.ready ? (
              <Link
                to="/c/$slug/setup/des/new"
                params={params}
                search={keepFocus}
                className={cn(buttonVariants({ size: "sm" }))}
                data-slot="roster-add"
              >
                <PlusIcon aria-hidden="true" />
                Add digital employee
              </Link>
            ) : null
          }
          className="m-6"
        />
      ) : (
        <>
          {runs.isError ? (
            <div className="px-6 pt-3 max-md:px-4">
              <LoadError what="Runs" error={runs.error} />
            </div>
          ) : null}
          <div data-slot="setup-des">
            {shown.map((d) => (
              <AgentRow key={d.membership_id} api={api} company={company} de={d} live={live} />
            ))}
            {q && !shown.length ? (
              <p className="px-6 py-3 text-sm text-muted-foreground max-md:px-4">
                No agent matches "{search.trim()}".
              </p>
            ) : null}
          </div>
          <LoadMore
            what="digital employees"
            hasMore={des.hasNextPage}
            loading={des.isFetchingNextPage}
            onMore={() => void des.fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}

function AgentRow({
  api,
  company,
  de,
  live,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeSummary;
  live: Live | null;
}) {
  const qc = useQueryClient();
  const manager = isManager(company);
  // WORKFORCE: whoever stands above the DE (or a manager) may pause and resume it.
  const intervene = manager || de.can_intervene === true;
  const latest = live?.latest.get(de.membership_id);
  const status = agentStatus(de.status, latest);
  const today = live?.today.get(de.membership_id);
  const move = useMutation({
    mutationFn: (to: "paused" | "active") =>
      api.client.digitalEmployees.update({
        params: { co: company.id, id: de.membership_id },
        body: { status: to },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.digitalEmployees(company.id) }),
  });
  // Retire and Remove are final: they confirm in a dialog (Remove asks for the name typed).
  const lifecycle = useLifecycle(api, company.id, de);
  const final = manager ? lifecycle.moves.filter((m) => m === "retire" || m === "remove") : [];
  // PURGE-RETIRED-AGENTS: a retired DE (removed or not) is removed for good by a manager.
  const purge = usePurge(api, company.id, {
    kind: "digital_employee",
    id: de.membership_id,
    name: de.display_name,
  });
  const id = { slug: company.slug, id: de.membership_id };
  const now = latest
    ? isLive(latest.state)
      ? `${RUN_STATE_LABELS[latest.state]} · ${formatElapsed(latest.started_at ?? latest.created_at)}`
      : `Last run ${RUN_STATE_LABELS[latest.state].toLowerCase()}`
    : live
      ? live.loading
        ? "…"
        : "No runs yet"
      : null;
  return (
    <>
      <ListRow
        data-row
        data-de={de.membership_id}
        data-agent-status={status}
        lead={
          <AvatarTile
            name={workerName(de.display_name)}
            kind="agent"
            size="xs"
            aria-hidden="true"
          />
        }
        title={workerName(de.display_name)}
        render={<Link to="/c/$slug/agents/de/$id" params={id} search={keepFocus} />}
        chips={
          <>
            <WorkerBadge kind="de" size="sm" />
            <Badge variant="outline" className="max-sm:hidden">
              {de.seat?.title ?? "No seat"}
            </Badge>
            {/* DE-WIZARD: readiness (Draft → Awaiting approval → Deploying → Live / Not live). */}
            {/* WF-FIX: no activity on a row, so a live DE reads "Active" (Live is earned). */}
            <ReadinessBadge readiness={de.readiness} unobserved />
            {/* WALLET-P1 §6.3: paused because the company's credit ran out. */}
            {de.status === "paused" && de.pause_reason === "wallet" ? (
              <Badge variant="warning" data-slot="no-credit">
                Paused · no credit
              </Badge>
            ) : null}
            {deployedUnverified(de) ? (
              <Badge variant="warning" className="max-md:hidden">
                Unverified
              </Badge>
            ) : null}
          </>
        }
        meta={de.readiness.state === "live" ? now : (readinessReason(de.readiness) ?? now)}
        trailing={
          live ? (
            <span className="tabular-nums" data-slot="today">
              {live.loading ? (
                "…"
              ) : today ? (
                <>
                  {`${today.runs} run${today.runs === 1 ? "" : "s"} · `}
                  <UsageCost usage={today} currency={live.currency} hideTokens={live.walletOn} />
                </>
              ) : (
                "0 runs today"
              )}
            </span>
          ) : null
        }
        actions={
          <>
            <MessageAction
              api={api}
              company={company}
              membershipId={de.membership_id}
              name={workerName(de.display_name)}
            />
            {intervene && de.status === "active" ? (
              <RowAction
                icon={PauseIcon}
                label={`Pause ${workerName(de.display_name)}`}
                disabled={move.isPending}
                onClick={() => move.mutate("paused")}
              />
            ) : intervene && de.status === "paused" ? (
              <RowAction
                icon={PlayIcon}
                label={`Resume ${workerName(de.display_name)}`}
                disabled={move.isPending}
                onClick={() => move.mutate("active")}
              />
            ) : null}
            {final.map((m) => (
              <RowAction
                key={m}
                icon={MOVES[m].icon}
                label={`${MOVES[m].label} ${workerName(de.display_name)}`}
                onClick={() => lifecycle.start(m)}
              />
            ))}
            {manager && de.status === "retired" ? (
              <RowAction
                icon={EraserIcon}
                label={`Remove ${workerName(de.display_name)} permanently`}
                onClick={purge.start}
                data-slot="purge-de"
              />
            ) : null}
            {live ? (
              <RowLinkAction
                icon={ActivityIcon}
                label="Runs"
                to="/c/$slug/agents/de/$id"
                params={id}
                search={(prev: Record<string, unknown>) => ({ ...keepFocus(prev), tab: "runs" })}
              />
            ) : null}
            {manager ? (
              <RowLinkAction
                icon={KeyRoundIcon}
                label="Keys"
                to="/c/$slug/agents/de/$id"
                params={id}
                search={(prev: Record<string, unknown>) => ({ ...keepFocus(prev), tab: "keys" })}
              />
            ) : null}
            <RowLinkAction
              icon={WalletIcon}
              label="Cost"
              to="/c/$slug/agents/de/$id"
              params={id}
              search={(prev: Record<string, unknown>) => ({ ...keepFocus(prev), tab: "cost" })}
            />
          </>
        }
      />
      {lifecycle.dialog}
      {purge.dialog}
    </>
  );
}
